import React, { useEffect, useCallback, useReducer, useRef } from 'react';
import { useMedicalCheckupListSync } from '../../hooks/useMedicalCheckupListSync';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Box, Typography, TextField, IconButton, InputAdornment, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { CompanyDto } from '../../models/companies/companyDto';
import { DateUtils } from '../../helpers/dateUtils';
import { medicalCheckupsService } from '../../services/medicalCheckupsService';
import { MedicalCheckupResult, MedicalCheckupResultUtil } from '../../models/enums/medicalCheckupResult';
import CompanyAutocomplete from '../common/CompanyAutocomplete';
import { MedicalCheckupShortDto } from '../../models/medicalCheckups/medicalCheckupShortDto';
import { MedicalCheckupTypeUtil } from '../../models/enums/medicalCheckupType ';
import { useNavigate } from 'react-router-dom';
import { LookupDto } from '../../models/common/lookup.dto';
import { terminalsService } from '../../services/terminalsService';
import { MedicalCheckupStatus } from '../../models/enums/medicalCheckupStatus';
import TmEye from '../../icons/TmEye';
import { RoleType } from '../../models/enums/roleType';
import { ShowForRoles } from '../common/permissions/ShowForRoles';
import { filterStorageService } from '../../services/filterStorageService';
import { FilterSection } from '../../models/enums/filterSection';


const initialState = {
    data: [] as MedicalCheckupShortDto[],
    terminals: [] as LookupDto[],
    page: 0,
    rowsPerPage: 15,
    dataCount: 0,
    loading: true,
    filter: {
        fio: '',
        company: null as CompanyDto | null,
        terminalIds: [] as number[],
    },
};

type State = typeof initialState;

type Action =
    | { type: 'SET_DATA'; payload: { data: MedicalCheckupShortDto[]; dataCount: number } }
    | { type: 'SET_TERMINALS'; payload: LookupDto[] }
    | { type: 'SET_PAGE'; payload: number }
    | { type: 'SET_ROWS_PER_PAGE'; payload: number }
    | { type: 'SET_FILTER'; payload: Partial<State['filter']> }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'PATCH_DATA'; payload: { data: MedicalCheckupShortDto[]; dataCount: number } };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'SET_DATA':
            return { ...state, data: action.payload.data, dataCount: action.payload.dataCount, loading: false };
        case 'SET_TERMINALS':
            return { ...state, terminals: action.payload };
        case 'SET_PAGE':
            return { ...state, page: action.payload, loading: true };
        case 'SET_ROWS_PER_PAGE':
            return { ...state, rowsPerPage: action.payload, page: 0, loading: true };
        case 'SET_FILTER':
            return { ...state, filter: { ...state.filter, ...action.payload }, page: 0, loading: true };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'PATCH_DATA':
            return { ...state, data: action.payload.data, dataCount: action.payload.dataCount, loading: false };
        default:
            return state;
    }
};

export const NewMedicalCheckupsTable = () => {
    const [state, dispatch] = useReducer(reducer, initialState, (baseState) => ({
        ...baseState,
        filter: filterStorageService.loadFilter(FilterSection.NewMedicalCheckups, baseState.filter),
    }));
    const navigate = useNavigate();
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        terminalsService.search({
            nameWithAddress: '',
            page: 0,
            pageSize: 10
        })
            .then(response => {
                dispatch({ type: 'SET_TERMINALS', payload: response.data.data.items });
            })
            .catch(error => {
                console.error('Error fetching terminals:', error);
            });
    }, []);

    useEffect(() => {
        filterStorageService.saveFilter(FilterSection.NewMedicalCheckups, state.filter);
    }, [state.filter]);

    const fetchData = useCallback(() => {
        medicalCheckupsService.getByFilter({
            fromDate: null,
            toDate: null,
            medicResult: null,
            statuses: [MedicalCheckupStatus.New, MedicalCheckupStatus.SigningError],
            fio: state.filter.fio,
            companyId: state.filter.company ? state.filter.company.id : null,
            terminalIds: state.filter.terminalIds,
            page: state.page,
            pageSize: state.rowsPerPage,
        })
            .then(r => {
                dispatch({ type: 'SET_DATA', payload: { data: r.data.data.items, dataCount: r.data.data.count } });
            })
            .catch(e => console.log(e));
    }, [state.filter, state.page, state.rowsPerPage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useMedicalCheckupListSync({
        listType: 'new',
        filter: state.filter,
        page: state.page,
        rowsPerPage: state.rowsPerPage,
        onPatch: (items, count) => dispatch({ type: 'PATCH_DATA', payload: { data: items, dataCount: count } }),
        onRefetch: fetchData,
        getCurrentState: () => ({ items: stateRef.current.data, count: stateRef.current.dataCount }),
    });

    const handleChangePage = (event: any, newPage: number) => {
        dispatch({ type: 'SET_PAGE', payload: newPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        dispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) });
    };

    const filterChanged = (name: string, value: any) => {
        dispatch({ type: 'SET_FILTER', payload: { [name]: value } });
    };

    function renderCheckupResult(result: MedicalCheckupResult) {
        const strValue = MedicalCheckupResultUtil.getStringValue(result);
        let color = result === MedicalCheckupResult.Rejected ? '#ec4a71' : '#5bba64';
        let backgroundColor = result === MedicalCheckupResult.Rejected ? '#fce4ea' : '#edfbee';

        return <Box display="inline-block" color={color} sx={{ backgroundColor: { backgroundColor }, borderRadius: "25px", paddingLeft: "15px", paddingRight: "15px" }}>{strValue}</Box>;
    };

    return (
        <Box>
            <Box display="flex" alignItems="center" marginBottom={1} mt={2.5}>
                <TextField
                    label="ФИО сотрудника"
                    name="fio"
                    value={state.filter.fio}
                    onChange={(e: { target: { name: string; value: any; }; }) => filterChanged(e.target.name, e.target.value)}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    style={{ width: 200, marginRight: 10 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => dispatch({ type: 'SET_PAGE', payload: 0 })}>
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        style: {
                            borderRadius: "10px",
                        },
                    }}
                />
                <CompanyAutocomplete
                    company={state.filter.company}
                    onCompanyChange={(e) => filterChanged('company', e)}
                    style={{ width: 200, marginRight: 10, }}
                />

                <TextField
                    select
                    label="Терминал"
                    name="terminalIds"
                    value={state.filter.terminalIds}
                    onChange={(e: { target: { name: string; value: any; }; }) => filterChanged(e.target.name, e.target.value)}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    style={{ width: 200, marginRight: 10 }}
                    SelectProps={{
                        multiple: true, style: {
                            borderRadius: "10px",
                        },
                    }}
                >
                    {state.terminals.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                            {t.name}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            <TableContainer sx={{ '.MuiTableCell-root': { py: 1.5 }, }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>№</Typography></TableCell>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>ФИО сотрудника</Typography></TableCell>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Клиент</Typography></TableCell>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Тип осмотра</Typography></TableCell>
                            <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Дата</Typography></TableCell>
                            <ShowForRoles roles={[RoleType.Admin, RoleType.Medic]}>
                                <TableCell><Typography variant="body1" sx={{ color: "#7c7e92" }}>Результат авто</Typography></TableCell>
                            </ShowForRoles>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {state.data.map((item) => (
                            <TableRow
                                key={item.id}
                                onClick={() => navigate(`/medical-checkups/${item.id}`)}
                                sx={{ '&:hover': { cursor: 'pointer', backgroundColor: 'rgba(0, 0, 0, 0.08)' } }}
                            >
                                <TableCell sx={{ fontWeight: "bold" }}>{item.id}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{item.fio}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{item.company.name}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>{MedicalCheckupTypeUtil.getStringValue(item.type)}</TableCell>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                    {DateUtils.formatDateTimeWithTimeZone(item.date, item.timeShift)}
                                </TableCell>
                                <ShowForRoles roles={[RoleType.Admin, RoleType.Medic]}>
                                    <TableCell sx={{ fontWeight: "bold" }}>{renderCheckupResult(item.autoResult)}</TableCell>
                                </ShowForRoles>
                                <TableCell sx={{ fontWeight: "bold" }}>{item.isReviewing && <TmEye stroke="#292d32" />}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>Всего: {state.dataCount}</Typography>
                <TablePagination
                    rowsPerPageOptions={[15, 30, 50]}
                    component="div"
                    count={state.dataCount}
                    rowsPerPage={state.rowsPerPage}
                    page={state.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={"Строк на странице:"}
                    labelDisplayedRows={({ from, to, count }: any): string => `Показано ${from}-${to} из ${count}`}
                />
            </Box>
        </Box>
    );
};